import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import FilterPanel from '../components/FilterPanel';
import ProductGrid from '../components/ProductGrid';
import { ENDPOINTS } from '../config';

const BASE_FILTERS = { isBestSeller: 'true' };

export default function BestSellers() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

    // Read ALL filters from URL query params
    const filters = Object.fromEntries(searchParams.entries());
    if (!filters.sort) filters.sort = 'newest';
    const currentPage = parseInt(filters.page) || 1;

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams(searchParams);
            params.set('isBestSeller', 'true');
            params.set('limit', '20');

            const response = await fetch(`${ENDPOINTS.PRODUCTS.PUBLIC}?${params.toString()}`);
            const data = await response.json();

            if (data?.isOk && data?.data) {
                setProducts(data.data.products || []);
                setPagination(data.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
            } else {
                setProducts([]);
            }
        } catch (err) {
            console.error('Error fetching best sellers:', err);
            setError('Failed to load products. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchParams.toString()]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const updateFilters = (newFilters) => {
        const params = new URLSearchParams(searchParams);

        Object.entries(newFilters).forEach(([key, value]) => {
            if (value && value !== '' && value !== 0 && value !== '0') {
                params.set(key, String(value));
            } else {
                params.delete(key);
            }
        });

        if (!('page' in newFilters)) {
            params.delete('page');
        }

        setSearchParams(params, { replace: true, preventScrollReset: true });
    };

    const handleSortChange = (newSort) => {
        updateFilters({ sort: newSort });
    };

    return (
        <main className="pt-[10px] pb-12">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Breadcrumb */}
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li><i className="ri-arrow-right-s-line"></i></li>
                        <li className="text-foreground font-medium">Best Sellers</li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground mb-2">Best Sellers</h1>
                        <p className="text-muted-foreground">
                            {loading ? 'Loading...' : `${pagination.total} product${pagination.total !== 1 ? 's' : ''} found`}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileFilterOpen(true)}
                            className="lg:hidden flex items-center gap-2 px-4 py-2 border-2 border-border rounded-lg hover:border-primary transition-colors whitespace-nowrap"
                        >
                            <i className="ri-filter-3-line"></i> Filters
                        </button>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-muted-foreground whitespace-nowrap">Sort by:</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => handleSortChange(e.target.value)}
                                className="px-4 py-2 border-2 border-border rounded-lg text-sm cursor-pointer outline-none hover:border-primary transition-colors"
                            >
                                <option value="newest">Newest First</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                                <option value="name_asc">Name: A to Z</option>
                                <option value="name_desc">Name: Z to A</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex gap-6">
                    {/* Desktop Filter Panel */}
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <FilterPanel filters={filters} onFilterChange={updateFilters} baseFilters={BASE_FILTERS} />
                    </div>

                    {/* Mobile Filter Drawer */}
                    {mobileFilterOpen && (
                        <div className="fixed inset-0 z-50 lg:hidden">
                            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFilterOpen(false)}></div>
                            <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-card overflow-y-auto">
                                <div className="sticky top-0 bg-card border-b border-border px-4 py-4 flex items-center justify-between">
                                    <h2 className="text-lg font-bold">Filters</h2>
                                    <button
                                        onClick={() => setMobileFilterOpen(false)}
                                        className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-lg transition-colors"
                                    >
                                        <i className="ri-close-line text-xl"></i>
                                    </button>
                                </div>
                                <div className="p-4">
                                    <FilterPanel filters={filters} onFilterChange={(f) => { updateFilters(f); setMobileFilterOpen(false); }} baseFilters={BASE_FILTERS} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Product Grid Area */}
                    <div className="flex-1">
                        {loading && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
                                        <div className="w-full h-64 bg-muted"></div>
                                        <div className="p-4 space-y-3">
                                            <div className="h-3 bg-muted rounded w-1/4"></div>
                                            <div className="h-4 bg-muted rounded w-3/4"></div>
                                            <div className="h-6 bg-muted rounded w-1/3"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {error && (
                            <div className="text-center py-16">
                                <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                                <p className="text-muted-foreground mb-4">{error}</p>
                                <button
                                    onClick={fetchProducts}
                                    className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {!loading && !error && products.length === 0 && (
                            <div className="text-center py-16">
                                <i className="ri-inbox-line text-5xl text-muted-foreground/40 mb-4 block"></i>
                                <h3 className="text-xl font-semibold text-foreground mb-2">No items available</h3>
                                <p className="text-muted-foreground mb-6">
                                    {filters.brand || filters.category || filters.search || filters.color
                                        ? 'No best sellers match the selected filters. Try removing some filters.'
                                        : 'Check back soon for our top picks!'}
                                </p>
                                {filters.brand || filters.category || filters.search || filters.color ? (
                                    <button
                                        onClick={() => setSearchParams({})}
                                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                                    >
                                        Clear All Filters
                                    </button>
                                ) : (
                                    <Link
                                        to="/products"
                                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all inline-block"
                                    >
                                        Browse All Products
                                    </Link>
                                )}
                            </div>
                        )}

                        {!loading && !error && products.length > 0 && (
                            <>
                                <ProductGrid products={products} />

                                {pagination.totalPages > 1 && (
                                    <div className="flex justify-center items-center gap-2 mt-8">
                                        <button
                                            onClick={() => updateFilters({ page: currentPage - 1 })}
                                            disabled={currentPage <= 1}
                                            className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                                        >
                                            <i className="ri-arrow-left-s-line"></i>
                                        </button>

                                        {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                                            let pageNum;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => updateFilters({ page: pageNum })}
                                                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'bg-primary text-white'
                                                            : 'border border-border hover:border-primary'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => updateFilters({ page: currentPage + 1 })}
                                            disabled={currentPage >= pagination.totalPages}
                                            className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                                        >
                                            <i className="ri-arrow-right-s-line"></i>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
